import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const emptyLead = {
  name: '',
  email: '',
  company: '',
  budget: '',
  plan: '',
  product: '',
  timeline: '',
  message: '',
  status: 'new',
  notes: '',
  submittedAt: ''
};

const LeadsPage = () => {
  const { getToken } = useAuth();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [formData, setFormData] = useState(emptyLead);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    let filtered = leads;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((lead) =>
        lead.name.toLowerCase().includes(term) ||
        lead.email.toLowerCase().includes(term) ||
        (lead.company && lead.company.toLowerCase().includes(term))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((lead) => (lead.status || 'new') === statusFilter);
    }

    setFilteredLeads(filtered);
  }, [searchTerm, statusFilter, leads]);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';

  const authHeaders = (json = false) => ({
    Authorization: `Bearer ${getToken()}`,
    ...(json ? { 'Content-Type': 'application/json' } : {})
  });

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/admin/leads`, {
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      setFeedback('Could not load leads right now.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetEditor = () => {
    setFormData(emptyLead);
    setSelectedLead(null);
    setEditorMode('create');
    setIsEditorOpen(false);
  };

  const openCreateLead = () => {
    setFeedback('');
    setEditorMode('create');
    setSelectedLead(null);
    setFormData({
      ...emptyLead,
      submittedAt: new Date().toISOString().slice(0, 16)
    });
    setIsEditorOpen(true);
  };

  const openEditLead = (lead) => {
    setFeedback('');
    setEditorMode('edit');
    setSelectedLead(lead);
    setFormData({
      name: lead.name || '',
      email: lead.email || '',
      company: lead.company || '',
      budget: lead.budget || '',
      plan: lead.plan || '',
      product: lead.product || '',
      timeline: lead.timeline || '',
      message: lead.message || '',
      status: lead.status || 'new',
      notes: lead.notes || '',
      submittedAt: lead.submittedAt ? new Date(lead.submittedAt).toISOString().slice(0, 16) : ''
    });
    setIsEditorOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value
    }));
  };

  const saveLead = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setFeedback('');

    const payload = {
      ...formData,
      submittedAt: formData.submittedAt ? new Date(formData.submittedAt).toISOString() : new Date().toISOString()
    };

    try {
      const url = editorMode === 'create'
        ? `${apiUrl}/api/admin/leads`
        : `${apiUrl}/api/admin/leads/${selectedLead.id}`;
      const method = editorMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: authHeaders(true),
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to save lead.');
      }

      await fetchLeads();
      setFeedback(editorMode === 'create' ? 'Lead created.' : 'Lead updated.');
      resetEditor();
    } catch (error) {
      console.error('Error saving lead:', error);
      setFeedback(error.message || 'Unable to save lead.');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteLead = async (lead) => {
    const confirmed = window.confirm(`Delete lead for ${lead.name}? This cannot be undone.`);
    if (!confirmed) return;

    try {
      const response = await fetch(`${apiUrl}/api/admin/leads/${lead.id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Unable to delete lead.');
      }

      if (selectedLead?.id === lead.id) {
        setSelectedLead(null);
      }

      setLeads((current) => current.filter((item) => item.id !== lead.id));
      setFeedback('Lead deleted.');
    } catch (error) {
      console.error('Error deleting lead:', error);
      setFeedback(error.message || 'Unable to delete lead.');
    }
  };

  const exportLeads = () => {
    const csvContent = [
      ['ID', 'Name', 'Email', 'Company', 'Plan', 'Product', 'Status', 'Date'],
      ...filteredLeads.map((lead) => [
        lead.id,
        lead.name,
        lead.email,
        lead.company || '',
        lead.plan || '',
        lead.product || '',
        lead.status || 'new',
        new Date(lead.submittedAt).toLocaleDateString()
      ])
    ].map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold font-nunito text-white">Leads</h1>
          <p className="mt-1 text-orange-300 font-nunito">{leads.length} total leads</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={openCreateLead}
            className="px-4 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-600 transition font-nunito"
          >
            New Lead
          </button>
          <button
            onClick={exportLeads}
            className="px-4 py-2 bg-white/10 border border-white/10 text-orange-100 rounded-lg hover:bg-white/15 transition font-nunito"
          >
            Export CSV
          </button>
        </div>
      </div>

      {feedback ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200 font-nunito">
          {feedback}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.9fr)]">
        <div className="space-y-6">
          <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                  Search
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, or company..."
                  className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white placeholder-orange-400/50 focus:outline-none focus:border-amber-500/50 font-nunito"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-amber-500/50 font-nunito"
                >
                  <option value="all">All Statuses</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="proposal">Proposal Sent</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-orange-900/40 border border-white/10 rounded-2xl overflow-hidden">
            {filteredLeads.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-black/30">
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-6 text-sm font-semibold text-orange-200 font-nunito">Lead</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-orange-200 font-nunito">Company</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-orange-200 font-nunito">Status</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-orange-200 font-nunito">Submitted</th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-orange-200 font-nunito">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-white/5 hover:bg-white/5 transition">
                        <td className="py-4 px-6">
                          <button
                            onClick={() => setSelectedLead(lead)}
                            className="text-left"
                          >
                            <div className="text-white font-nunito font-medium">{lead.name}</div>
                            <div className="text-orange-300 font-nunito text-sm">{lead.email}</div>
                          </button>
                        </td>
                        <td className="py-4 px-6 text-orange-300 font-nunito">{lead.company || '-'}</td>
                        <td className="py-4 px-6">
                          <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/20 font-nunito">
                            {lead.status || 'new'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-orange-300 font-nunito">
                          {new Date(lead.submittedAt).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex flex-wrap gap-3 text-sm font-nunito">
                            <button
                              onClick={() => setSelectedLead(lead)}
                              className="text-orange-200 hover:text-white transition"
                            >
                              View
                            </button>
                            <button
                              onClick={() => openEditLead(lead)}
                              className="text-amber-300 hover:text-amber-200 transition"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteLead(lead)}
                              className="text-red-300 hover:text-red-200 transition"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-orange-400 font-nunito">No leads found</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold font-nunito text-white">Lead Details</h2>
                <p className="mt-1 text-sm text-orange-300 font-nunito">
                  Select a lead to review details, notes, and project context.
                </p>
              </div>
              {selectedLead ? (
                <button
                  onClick={() => openEditLead(selectedLead)}
                  className="px-3 py-2 bg-white/10 border border-white/10 text-orange-100 rounded-lg hover:bg-white/15 transition font-nunito text-sm"
                >
                  Edit
                </button>
              ) : null}
            </div>

            {selectedLead ? (
              <div className="mt-6 space-y-4">
                <div>
                  <label className="text-sm text-orange-300 font-nunito">Name</label>
                  <p className="text-white font-nunito mt-1">{selectedLead.name}</p>
                </div>
                <div>
                  <label className="text-sm text-orange-300 font-nunito">Email</label>
                  <p className="text-white font-nunito mt-1">{selectedLead.email}</p>
                </div>
                <div>
                  <label className="text-sm text-orange-300 font-nunito">Company</label>
                  <p className="text-white font-nunito mt-1">{selectedLead.company || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-orange-300 font-nunito">Status</label>
                  <p className="text-white font-nunito mt-1">{selectedLead.status || 'new'}</p>
                </div>
                <div>
                  <label className="text-sm text-orange-300 font-nunito">Budget / Plan / Product</label>
                  <p className="text-white font-nunito mt-1">
                    {[selectedLead.budget, selectedLead.plan, selectedLead.product].filter(Boolean).join(' • ') || '-'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-orange-300 font-nunito">Timeline</label>
                  <p className="text-white font-nunito mt-1">{selectedLead.timeline || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-orange-300 font-nunito">Message</label>
                  <p className="text-white font-nunito mt-1 whitespace-pre-wrap">{selectedLead.message || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-orange-300 font-nunito">Notes</label>
                  <p className="text-white font-nunito mt-1 whitespace-pre-wrap">{selectedLead.notes || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-orange-300 font-nunito">Submitted</label>
                  <p className="text-white font-nunito mt-1">{new Date(selectedLead.submittedAt).toLocaleString()}</p>
                </div>
                <div className="pt-3 flex gap-3">
                  <a
                    href={`mailto:${selectedLead.email}`}
                    className="flex-1 py-2 px-4 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-600 transition text-center font-nunito"
                  >
                    Send Email
                  </a>
                  <button
                    onClick={() => deleteLead(selectedLead)}
                    className="px-4 py-2 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg hover:bg-red-500/30 transition font-nunito"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6 rounded-xl border border-dashed border-white/10 p-6 text-sm text-orange-300 font-nunito">
                Pick a lead from the table or create a new one to start managing your pipeline.
              </div>
            )}
          </div>
        </div>
      </div>

      {isEditorOpen ? (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-orange-950/95 border border-white/10 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={saveLead} className="p-6 space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold font-nunito text-white">
                    {editorMode === 'create' ? 'Create Lead' : 'Edit Lead'}
                  </h2>
                  <p className="mt-1 text-sm text-orange-300 font-nunito">
                    Manage the full lead record, including contact details, notes, and pipeline status.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={resetEditor}
                  className="text-orange-300 hover:text-white transition"
                >
                  Close
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name *">
                  <input name="name" value={formData.name} onChange={handleChange} required className={inputClass} />
                </Field>
                <Field label="Email *">
                  <input name="email" type="email" value={formData.email} onChange={handleChange} required className={inputClass} />
                </Field>
                <Field label="Company">
                  <input name="company" value={formData.company} onChange={handleChange} className={inputClass} />
                </Field>
                <Field label="Budget">
                  <input name="budget" value={formData.budget} onChange={handleChange} className={inputClass} />
                </Field>
                <Field label="Plan">
                  <input name="plan" value={formData.plan} onChange={handleChange} className={inputClass} />
                </Field>
                <Field label="Product">
                  <input name="product" value={formData.product} onChange={handleChange} className={inputClass} />
                </Field>
                <Field label="Timeline">
                  <input name="timeline" value={formData.timeline} onChange={handleChange} className={inputClass} />
                </Field>
                <Field label="Status">
                  <select name="status" value={formData.status} onChange={handleChange} className={inputClass}>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="proposal">Proposal Sent</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                  </select>
                </Field>
                <Field label="Submitted">
                  <input name="submittedAt" type="datetime-local" value={formData.submittedAt} onChange={handleChange} className={inputClass} />
                </Field>
              </div>

              <Field label="Project Message *">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  required
                  className={`${inputClass} resize-y`}
                />
              </Field>

              <Field label="Internal Notes">
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="4"
                  className={`${inputClass} resize-y`}
                />
              </Field>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={resetEditor}
                  className="px-4 py-2 bg-white/10 border border-white/10 text-orange-100 rounded-lg hover:bg-white/15 transition font-nunito"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-5 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-600 transition font-nunito disabled:opacity-60"
                >
                  {isSaving ? 'Saving...' : editorMode === 'create' ? 'Create Lead' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const inputClass = 'w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-amber-500/50 font-nunito';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm font-medium text-orange-200 font-nunito mb-2">{label}</span>
    {children}
  </label>
);

export default LeadsPage;
